import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./Cart.module.css";
import Api from "../../utils/Api/Api";
import { money, symbolFor } from "../../utils/format";
import { useAuthGate, hasSession } from "../../components/AuthModal/AuthGate";
import { TopBar } from "../../components/TopBar/TopBar";
import { CtaBar } from "../../components/CtaBar/CtaBar";
import { EmptyState, CartIcon } from "../../components/EmptyState/EmptyState";
import coinLogo from "../../asset/icons/fleek-coin-logo.png";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Cart, rebuilt from design/Purchase Flow.dc.html (isCart block).
 *
 * The cart holds a single plan. Removing it empties the cart; the bar at the
 * bottom takes the customer to checkout.
 */
export const Cart = () => {
  const navigate = useNavigate();
  const { requireAuth } = useAuthGate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [removing, setRemoving] = useState(false);

  const signedIn = hasSession();

  const load = useCallback(async () => {
    if (!hasSession()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await Api.GetCartItems();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Cart failed to load:", err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = useCallback(async () => {
    if (removing || !rows.length) return;
    setRemoving(true);
    try {
      await Api.EmptyCart();
      setRows([]);
    } catch (err) {
      console.error("Cart failed to empty:", err);
      setFailed(true);
    } finally {
      setRemoving(false);
    }
  }, [removing, rows.length]);

  const item = rows[0];

  const view = useMemo(() => {
    if (!item) return null;
    const plan = item.subscription_plan || {};
    const currency = plan.currency_type;
    const cost = num(item.cost);
    const coins = num(item.coin_value_reserved);
    const payable = coins > 0 ? num(item.actual_discounted_cost) : cost;
    return {
      logo: plan.image_url || FleekIcon,
      brand: plan.service_name || item.product_name || "",
      plan: plan.plan_name || "",
      price: money(cost, currency),
      mrp: item.mrp_price ? money(item.mrp_price, currency) : "",
      coinPct: coins > 0 && cost > 0 ? Math.round((coins / cost) * 100) : 0,
      withCoins: money(payable, currency),
      payable: money(payable, currency),
      mrpBar: item.mrp_price ? money(item.mrp_price, currency) : "",
      // The earn-back figure is part of the coin model; the backend does not
      // yet return one. Shown only when it does.
      coinsEarned: item.coins_to_earn ?? item.earn_coins ?? null,
      symbol: symbolFor(currency),
    };
  }, [item]);

  const body = () => {
    if (!signedIn) {
      return (
        <EmptyState
          icon={<CartIcon />}
          title="Sign in to see your cart"
          body="Your cart is tied to your account."
          action="Sign in"
          onAction={() => requireAuth(() => window.location.reload(), "Sign in to see your cart.")}
        />
      );
    }
    if (loading) return <div className={styles.skeleton} />;
    if (failed) {
      return (
        <EmptyState
          icon={<CartIcon />}
          title="We couldn't load your cart"
          body="Check your connection and try again."
          action="Try again"
          onAction={load}
        />
      );
    }
    if (!view) {
      return (
        <EmptyState
          icon={<CartIcon />}
          title="Your cart is empty"
          body="Add a plan and it'll show up here."
          action="Browse brands"
          onAction={() => navigate("/marketplace")}
        />
      );
    }

    return (
      <>
        <div className={styles.item}>
          <img
            className={styles.logo}
            src={view.logo}
            alt=""
            onError={(e) => {
              e.currentTarget.src = FleekIcon;
            }}
          />
          <div className={styles.text}>
            <div className={styles.brand}>{view.brand}</div>
            {view.plan && <div className={styles.plan}>{view.plan}</div>}
            <div className={styles.prices}>
              <span className={styles.price}>{view.price}</span>
              {view.mrp && <span className={styles.mrp}>MRP {view.mrp}</span>}
            </div>
            {view.coinPct > 0 && (
              <div className={styles.coinLine}>
                <img src={coinLogo} alt="" className={styles.coinIcon} />
                <span className={styles.coinText}>
                  +{view.coinPct}% with coins → {view.withCoins}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            className={styles.remove}
            onClick={remove}
            disabled={removing}
            aria-label="Remove from cart"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 7h16" />
              <path d="M9 7V4h6v3" />
              <path d="M6 7l1 13h10l1-13" />
            </svg>
          </button>
        </div>

        {view.coinsEarned != null && (
          <div className={styles.earn}>
            <img src={coinLogo} alt="" className={styles.earnIcon} />
            <span className={styles.earnText}>
              You'll earn {Number(view.coinsEarned).toLocaleString("en-IN")} coins back — 20% of what you pay
            </span>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.page}>
      <TopBar title="Cart" backTo="/marketplace" />
      <div className={styles.shell}>{body()}</div>

      {view && (
        <>
          <CtaBar.Spacer />
          <CtaBar
            label="Total"
            amount={view.price}
            strike={view.mrpBar}
            text="Checkout"
            onClick={() => navigate("/checkout")}
          />
        </>
      )}
    </div>
  );
};

export default Cart;
